import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { NavigateNext, Lock } from '@mui/icons-material';
import { filesApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import LoadingButton from '../components/LoadingButton';
import { useModels } from '../hooks/useModels';
import { useUserSettings } from '../hooks/useUserSettings';

const SelectPages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const { models: AVAILABLE_MODELS, defaultModel } = useModels();
  // Only whitelisted users choose a model; everyone else gets the default
  const { canChooseModels, settings } = useUserSettings();
  const preferredModel = settings?.models?.pdf || defaultModel;

  const [password, setPassword] = useState('');
  const [selectedPages, setSelectedPages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [pages, setPages] = useState([]);
  const [requiresPassword, setRequiresPassword] = useState(searchParams.get('requiresPassword') === 'true');
  const [selectedModel, setSelectedModel] = useState('');

  // Set default model once loaded from API
  useEffect(() => {
    if (canChooseModels && preferredModel && !selectedModel) {
      setSelectedModel(preferredModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canChooseModels, preferredModel]);

  // Get fileId and accountType from URL params
  const fileId = searchParams.get('fileId');
  const accountType = searchParams.get('accountType');

  const handleProcess = async () => {
    if (!fileId) {
      showError('No file selected. Please upload a file first.');
      navigate('/upload');
      return;
    }

    setProcessing(true);
    try {
      console.log('🔐 Processing request:', { fileId, accountType, model: selectedModel, hasPassword: !!password, requiresPassword });
      
      const response = await filesApi.process({
        fileId: fileId,
        accountType: accountType,
        model: canChooseModels ? selectedModel || undefined : undefined,
        password: requiresPassword ? password : undefined,
        selectedPages: selectedPages.length > 0 ? selectedPages : undefined
      });

      console.log('✅ Process response:', response.data);

      if (response.data.requiresPassword) {
        console.log('🔒 Password required - showing password input');
        setRequiresPassword(true);
        return;
      }

      if (response.data.requiresPageSelection) {
        setPages(response.data.pages);
        return;
      }

      if (response.data.success && response.data.jobId) {
        // Job created successfully - navigate to processing jobs page
        showSuccess('Processing started! You can leave this page and come back later.');
        navigate('/processing-jobs');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Process File
      </Typography>

      {requiresPassword && (
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title="Password Required"
            avatar={<Lock color="warning" />}
          />
          <CardContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This PDF is password protected. Please provide the password to continue.
            </Alert>
            <TextField
              fullWidth
              label="PDF Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <LoadingButton
              variant="contained"
              loading={processing}
              onClick={handleProcess}
              disabled={!password.trim()}
            >
              Process with Password
            </LoadingButton>
          </CardContent>
        </Card>
      )}

      {canChooseModels && (
      <Card sx={{ mb: 3 }}>
        <CardHeader title="AI Model" />
        <CardContent>
          <FormControl fullWidth>
            <InputLabel id="model-select-label">AI Model</InputLabel>
            <Select
              labelId="model-select-label"
              value={selectedModel}
              label="AI Model"
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {AVAILABLE_MODELS.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            Different models have different speeds and accuracy. The recommended model is pre-selected.
          </Alert>
        </CardContent>
      </Card>
      )}

      {pages.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title="Select Pages to Process"
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const allPages = pages.map((_, i) => i + 1);
                    setSelectedPages(allPages);
                  }}
                  disabled={selectedPages.length === pages.length}
                >
                  Select All ({pages.length})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedPages([])}
                  disabled={selectedPages.length === 0}
                >
                  Clear All
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the pages you want to process. {selectedPages.length} of {pages.length} pages selected.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pages.map((page, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: selectedPages.includes(index + 1) ? '2px solid' : '1px solid',
                    borderColor: selectedPages.includes(index + 1) ? 'primary.main' : 'divider',
                    backgroundColor: selectedPages.includes(index + 1) ? 'action.selected' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => {
                    if (selectedPages.includes(index + 1)) {
                      setSelectedPages(selectedPages.filter(p => p !== index + 1));
                    } else {
                      setSelectedPages([...selectedPages, index + 1]);
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedPages.includes(index + 1)}
                      onChange={() => {}} // Handled by Paper onClick
                      sx={{ mt: -1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Page {index + 1}
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          backgroundColor: 'grey.50',
                          maxHeight: 150,
                          overflow: 'auto',
                          fontSize: '0.85rem',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          color: 'text.secondary'
                        }}
                      >
                        {page && page.trim().length > 0 
                          ? page.trim().slice(0, 500) + (page.trim().length > 500 ? '...' : '')
                          : '(Empty page)'}
                      </Paper>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <LoadingButton
            variant="contained"
            size="large"
            loading={processing}
            onClick={handleProcess}
            startIcon={<NavigateNext />}
            disabled={requiresPassword && !password.trim()}
          >
            {processing ? 'Processing...' : 'Continue Processing'}
          </LoadingButton>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SelectPages;
